import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  UI_LOCALES,
  UI_LOCALE_LOADERS,
  DEFAULT_UI_LOCALE,
  isUiLocale,
  resolveBrowserLocale,
} from '../locales'

// Interface (UI) language — separate from the question-bank language.
// Question language still lives under 'preferredLanguage' (see stores/game.js)
// and is intentionally left untouched by this store.
export const UI_LOCALE_STORAGE_KEY = 'koh-ui-locale'

// One-time i18n instance reference, set by main.js via bindI18n().
// Kept outside the store so this file has no import-order dependency
// on main.js, but the store is still the single place that mutates it.
let i18nInstance = null

// Locale messages are lazy-loaded (see locales/index.js). `en` ships in
// the initial bundle so it's always considered already-loaded; every
// other code is fetched on first use and cached here for the session.
const loadedLocales = new Set([DEFAULT_UI_LOCALE])

export const useLocaleStore = defineStore('locale', () => {
  const locale = ref(DEFAULT_UI_LOCALE)
  // True while a lazy locale chunk is being fetched — lets the UI show a
  // brief "loading" state on the switcher instead of silently doing
  // nothing while the network request is in flight.
  const loading = ref(false)

  const current = computed(() => UI_LOCALES.find((l) => l.code === locale.value) || UI_LOCALES[0])

  function bindI18n(i18n) {
    i18nInstance = i18n
  }

  // Loads and registers the messages for `code` with the i18n instance,
  // if not already loaded. `pt-BR` also pulls in `pt` so its fallback
  // chain (pt-BR -> pt -> en, see main.js) actually resolves instead of
  // silently skipping straight to English for any string not yet
  // translated in the Brazilian file.
  async function ensureLocaleLoaded(code) {
    const toLoad = code === 'pt-BR' && !loadedLocales.has('pt') ? ['pt', code] : [code]

    for (const c of toLoad) {
      if (loadedLocales.has(c) || !UI_LOCALE_LOADERS[c]) continue
      const mod = await UI_LOCALE_LOADERS[c]()
      if (i18nInstance) {
        i18nInstance.global.setLocaleMessage(c, mod.default)
      }
      loadedLocales.add(c)
    }
  }

  async function applyToI18n() {
    loading.value = true
    try {
      await ensureLocaleLoaded(locale.value)
    } finally {
      loading.value = false
    }
    if (i18nInstance) {
      i18nInstance.global.locale.value = locale.value
    }
    try {
      document.documentElement.lang = locale.value
    } catch {
      // ignore (SSR / no document)
    }
  }

  function detectInitialLocale() {
    // Priority: ?lang= query param > stored preference > browser language > default
    try {
      const params = new URLSearchParams(window.location.search)
      const fromQuery = params.get('lang')
      if (fromQuery && isUiLocale(fromQuery)) return fromQuery
    } catch {
      // ignore
    }

    try {
      const stored = localStorage.getItem(UI_LOCALE_STORAGE_KEY)
      if (stored && isUiLocale(stored)) return stored
    } catch {
      // localStorage unavailable
    }

    try {
      const browserLocales = navigator.languages && navigator.languages.length
        ? navigator.languages
        : [navigator.language]
      return resolveBrowserLocale(browserLocales)
    } catch {
      return DEFAULT_UI_LOCALE
    }
  }

  // Async because the initial locale may need a lazy-loaded chunk (e.g. a
  // returning visitor whose stored/browser preference is not English).
  // Callers in main.js don't await this — the app mounts immediately with
  // English messages already in place, and the UI swaps to the resolved
  // locale as soon as its chunk arrives (no blank-screen wait).
  async function init() {
    locale.value = detectInitialLocale()
    await applyToI18n()
  }

  async function setLocale(code) {
    if (!isUiLocale(code)) return
    locale.value = code
    try {
      localStorage.setItem(UI_LOCALE_STORAGE_KEY, code)
    } catch {
      // ignore
    }
    await applyToI18n()
  }

  return { locale, current, loading, bindI18n, init, setLocale }
})
